import { memo, useCallback } from 'react';
import clsx from 'clsx';

import HighlightedText from '../../highlighted-text/HighlightedText';
import Icon from '../../../icons/Icon';
import Emoji from '../../emoji/Emoji';

import '../CommonGroupedDropdown.scss';

const OptionItem = ({
  option,
  searchQuery,
  withIcons,
  isEmojiIcons,
  isHighlighted,
  onSelect,
  listboxId,
  emptyLabel,
}) => {
  const { value, label, disabled, Icon: OptionIcon, icon, iconClassName, isStrokeIcon } = option;

  const handleClick = useCallback(() => {
    if (!disabled) onSelect(value);
  }, [disabled, onSelect, value]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!disabled) onSelect(value);
      }
    },
    [disabled, onSelect, value],
  );

  return (
    <li
      id={`${listboxId}-option-${value}`}
      role='option'
      aria-selected={value === undefined ? undefined : isHighlighted}
      aria-disabled={disabled}
      className={clsx(
        'common-grouped-dropdown_item',
        withIcons && 'common-grouped-dropdown_item--with-icon',
        disabled && 'common-grouped-dropdown_item--disabled',
        !label && 'common-grouped-dropdown_item--empty',
        isHighlighted && 'common-grouped-dropdown_item--highlighted',
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}>
      <IconComponent
        icon={icon}
        iconClassName={iconClassName}
        isStrokeIcon={isStrokeIcon}
        isEmojiIcons={isEmojiIcons}
        withIcons={withIcons}
        OptionIcon={OptionIcon}
        value={value}
      />
      {label ? <HighlightedText value={label} searchTerm={searchQuery} /> : emptyLabel}
    </li>
  );
};

export default memo(OptionItem);

const IconComponent = ({ icon, iconClassName, isStrokeIcon, isEmojiIcons, withIcons, OptionIcon, value }) => {
  if (!withIcons) {
    return null;
  }

  if (icon && isEmojiIcons) {
    return <Emoji symbol={icon} label={value} />;
  }

  if (icon && !isEmojiIcons) {
    return (
      <Icon
        icon={icon}
        disableCommonColors
        className={clsx(
          'common-grouped-dropdown_item_icon',
          iconClassName,
          isStrokeIcon ? 'common-grouped-dropdown_item_icon--stroke' : 'common-grouped-dropdown_item_icon--fill',
        )}
      />
    );
  }

  if (OptionIcon) {
    return (
      <span className={clsx('common-grouped-dropdown_item_icon', iconClassName)}>
        <OptionIcon />
      </span>
    );
  }

  return <div className='common-grouped-dropdown_item_empty-icon' />;
};
